import { useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RouletteContentFirstVisitRetentionTable from '../../../../components/pointhome/roulette/content/RouletteContentFirstVisitRetentionTable';
import type { FirstVisitRetentionRow } from '../../../../components/pointhome/roulette/content/RouletteContentFirstVisitRetentionTable';

export type WelcomeCtaCard = {
  viewCount: number;
  clickCount: number;
  conversionRate: number;
};

export type WelcomeCta = {
  attendance: WelcomeCtaCard;
  mission: WelcomeCtaCard;
};

export type WelcomeFunnel = {
  popupViewCount: number;
  startClickCount: number;
  completeCount: number;
  startRate: number;
  completeRate: number;
  completeRateFromStart: number;
};

export type WelcomeRetention = {
  total: FirstVisitRetentionRow[];
  attendanceAndMission: FirstVisitRetentionRow[];
  attendanceOnly: FirstVisitRetentionRow[];
};

export type WelcomeAvgParticipationDaysSegment = {
  avgParticipationDays: number;
  cohortSize: number;
};

export type WelcomeAvgParticipationDays = {
  total: WelcomeAvgParticipationDaysSegment;
  attendanceAndMission: WelcomeAvgParticipationDaysSegment;
  attendanceOnly: WelcomeAvgParticipationDaysSegment;
};

export type WelcomePerfectAttendanceRateSegment = {
  perfectAttendanceRate: number;
  cohortSize: number;
};

export type WelcomePerfectAttendanceRate = {
  total: WelcomePerfectAttendanceRateSegment;
  attendanceAndMission: WelcomePerfectAttendanceRateSegment;
  attendanceOnly: WelcomePerfectAttendanceRateSegment;
};

type RetentionSegment = keyof WelcomeRetention;

const RETENTION_SEGMENT_OPTIONS: { value: RetentionSegment; label: string }[] = [
  { value: 'total', label: '전체' },
  { value: 'attendanceAndMission', label: '출석+미션' },
  { value: 'attendanceOnly', label: '출석만' },
];

type Props = {
  loading: boolean;
  error: string | null;
  cta: WelcomeCta | null;
  funnel: WelcomeFunnel | null;
  retention: WelcomeRetention | null;
  avgParticipationDays: WelcomeAvgParticipationDays | null;
  perfectAttendanceRate: WelcomePerfectAttendanceRate | null;
};

const ACCENT = '#0044D2';
const MAX_PARTICIPATION_WINDOW_DAYS = 8;

function formatCount(value: number): string {
  return value.toLocaleString('ko-KR');
}

function SegmentStatCard({
  label,
  value,
  unit,
  suffix,
  caption,
}: {
  label: string;
  value: number;
  unit: string;
  suffix?: string;
  caption: string;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT, flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography variant="h5" fontWeight={800} sx={{ color: ACCENT, lineHeight: 1 }}>
          {value}
        </Typography>
        <Typography variant="body1" fontWeight={700} sx={{ color: ACCENT }}>
          {unit}
        </Typography>
        {suffix && (
          <Typography variant="body1" fontWeight={700} color="text.secondary" sx={{ ml: 0.5 }}>
            {suffix}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {caption}
      </Typography>
    </Box>
  );
}

function CtaCard({
  title,
  denominatorLabel,
  numeratorLabel,
  card,
}: {
  title: string;
  denominatorLabel: string;
  numeratorLabel: string;
  card: WelcomeCtaCard;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        p: 2.5,
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            p: 1.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            {denominatorLabel}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#374151" sx={{ mt: 0.5 }}>
            {formatCount(card.viewCount)}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          →
        </Typography>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            p: 1.5,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            {numeratorLabel}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="#374151" sx={{ mt: 0.5 }}>
            {formatCount(card.clickCount)}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #d1d5db' }}
      >
        <Typography variant="body2" color="text.secondary">
          룰렛 이동 CTA 전환율
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ color: ACCENT }}>
          {card.conversionRate}%
        </Typography>
      </Stack>
    </Box>
  );
}

function FunnelRow({
  label,
  count,
  rate,
  rateCaption,
  widthPercent,
}: {
  label: string;
  count: number;
  rate: number;
  rateCaption: string;
  widthPercent: number;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Box sx={{ width: { xs: 96, sm: 140 }, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold">
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          height: 40,
          backgroundColor: '#eef1f6',
          borderRadius: 1.5,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${Math.max(0, Math.min(100, widthPercent))}%`,
            backgroundColor: ACCENT,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            transition: 'width 0.3s ease',
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ color: '#fff', whiteSpace: 'nowrap' }}>
            {formatCount(count)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: 88, flexShrink: 0, textAlign: 'right' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {rate}%
        </Typography>
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ fontSize: '0.8rem', color: '#475569' }}
        >
          {rateCaption}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function TabFirstVisit({
  loading,
  error,
  cta,
  funnel,
  retention,
  avgParticipationDays,
  perfectAttendanceRate,
}: Props) {
  const [retentionSegment, setRetentionSegment] = useState<RetentionSegment>('total');

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ mb: 2 }}>
        {error}
      </Typography>
    );
  }
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!cta || !funnel) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
        데이터가 없습니다
      </Typography>
    );
  }

  return (
    <Stack spacing={4}>
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <CtaCard
            title="출석 보상 → 룰렛 이동"
            denominatorLabel="출석 보상 팝업 노출"
            numeratorLabel="룰렛 돌리기 클릭"
            card={cta.attendance}
          />
          <CtaCard
            title="미션 보상 → 룰렛 이동"
            denominatorLabel="미션 보상 획득"
            numeratorLabel="룰렛 돌리기 클릭"
            card={cta.mission}
          />
        </Stack>
      </Box>

      <Box>
        <Box
          sx={{
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            p: 2.5,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 2 }}>
            미션 팝업 → 참여 → 완료
          </Typography>

          <Stack spacing={1.5}>
            <FunnelRow
              label="미션 팝업 노출"
              count={funnel.popupViewCount}
              rate={100}
              rateCaption="기준"
              widthPercent={100}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ pl: { xs: 12, sm: 20 }, fontSize: '0.8rem', color: '#475569' }}
            >
              미션 참여 전환 {funnel.startRate}%
            </Typography>
            <FunnelRow
              label="미션 도전하기"
              count={funnel.startClickCount}
              rate={funnel.startRate}
              rateCaption="VS 팝업노출"
              widthPercent={funnel.startRate}
            />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ pl: { xs: 12, sm: 20 }, fontSize: '0.8rem', color: '#475569' }}
            >
              미션 완수 전환 {funnel.completeRateFromStart}%
            </Typography>
            <FunnelRow
              label="미션 보상 획득"
              count={funnel.completeCount}
              rate={funnel.completeRate}
              rateCaption="VS 팝업노출"
              widthPercent={funnel.completeRate}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #d1d5db' }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              팝업 대비 최종 완료
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: ACCENT }}>
              {funnel.completeRate}%
            </Typography>
          </Stack>
        </Box>
      </Box>

      {avgParticipationDays && perfectAttendanceRate && (
        <Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SegmentStatCard
              label="평균 참여 횟수 · 출석+미션"
              value={avgParticipationDays.attendanceAndMission.avgParticipationDays}
              unit="일"
              suffix={`/ ${MAX_PARTICIPATION_WINDOW_DAYS}일`}
              caption="D0~D7 중 평균 출석 일수"
            />
            <SegmentStatCard
              label="평균 참여 횟수 · 출석만"
              value={avgParticipationDays.attendanceOnly.avgParticipationDays}
              unit="일"
              suffix={`/ ${MAX_PARTICIPATION_WINDOW_DAYS}일`}
              caption="D0~D7 중 평균 출석 일수"
            />
            <SegmentStatCard
              label="D0~D7 개근 비율 · 출석+미션"
              value={perfectAttendanceRate.attendanceAndMission.perfectAttendanceRate}
              unit="%"
              caption="8일 전부 출석한 사용자 비율"
            />
            <SegmentStatCard
              label="D0~D7 개근 비율 · 출석만"
              value={perfectAttendanceRate.attendanceOnly.perfectAttendanceRate}
              unit="%"
              caption="8일 전부 출석한 사용자 비율"
            />
          </Stack>
        </Box>
      )}

      {retention && (
        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                일별 리텐션
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={retentionSegment}
              exclusive
              size="small"
              onChange={(_, value: RetentionSegment | null) => {
                if (value) setRetentionSegment(value);
              }}
              sx={{
                backgroundColor: '#f1f5f9',
                p: 0.5,
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#64748b',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: '#fff',
                  color: ACCENT,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                },
              }}
            >
              {RETENTION_SEGMENT_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          <RouletteContentFirstVisitRetentionTable rows={retention[retentionSegment]} />
        </Box>
      )}
    </Stack>
  );
}
